/**
 * Record report visits / exports in company activity logs (module: Reports).
 * Failures are swallowed so logging never blocks the report UI.
 */
import axios from 'axios';
import logger from '@/utils/logger';

/** Route name → friendly label shown in Users Activity Log */
export const REPORT_ACTIVITY_LABELS = {
  ReportsHome: 'Reports Home',
  ProfitAndLoss: 'Profit & Loss',
  ProfitAndLossAccountDetail: 'Profit & Loss — Account Detail',
  BalanceSheet: 'Balance Sheet',
  CashFlowReport: 'Cash Flow',
  TrialBalance: 'Trial Balance',
  GeneralLedgerReport: 'General Ledger',
  TransactionDetailByAccount: 'Transaction Detail by Account',
  ExpenseReport: 'Expense Report',
  IncomeByCustomerDetail: 'Income by Customer',
  AutomaticTransactions: 'Automatic Transactions',
  ReconciliationReports: 'Reconciliation',
  AandRAgingSummaryReport: 'A/R Aging Summary',
  AandRAgingDetailReport: 'A/R Aging Detail',
  CustomerrStatementReport: 'Customer Statement',
  CustomerTransactionHistoryReport: 'Customer Transaction History',
  AandPAgingSummaryReport: 'A/P Aging Summary',
  AandPAgingDetailReport: 'A/P Aging Detail',
  VendorsStatementReport: 'Vendor Statement',
  VendorTransactionHistoryReport: 'Vendor Transaction History',
  SalesByCustomerSummary: 'Sales by Customer Summary',
  SalesByCustomerDetail: 'Sales by Customer Detail',
  SalesByItemSummary: 'Sales by Item Summary',
  SalesByItemDetail: 'Sales by Item Detail',
  PurchasebyVendorSummary: 'Purchase by Vendor Summary',
  PurchasebyVendorDetail: 'Purchase by Vendor Detail',
  PurchasebyItemSummary: 'Purchase by Item Summary',
  PurchasebyItemDetail: 'Purchase by Item Detail',
  BudgetOverviewReport: 'Budget Overview',
  BudgetvsActualsReport: 'Budget vs Actuals',
  DepreciationReport: 'Depreciation',
  InventoryDetails: 'Inventory Details',
  InventoryStockStatusByItem: 'Inventory Stock Status by Item',
  InventoryValuationDetails: 'Inventory Valuation Details',
  InventoryValuationSummary: 'Inventory Valuation Summary',
  CheckDetail: 'Check Detail',
  DepositDetail: 'Deposit Detail',
  ReconciliationDiscrepancy: 'Reconciliation Discrepancy',
  MissingChecks: 'Missing Checks',
  TaxSummaryReport: 'Tax Summary',
  PayHeadDetail: 'Pay Head Detail',
  PayHeadSummary: 'Pay Head Summary',
  PaySheet: 'Pay Sheet',
  PaySlipDetail: 'Pay Slip Detail',
  PaySlipSummary: 'Pay Slip Summary',
};

function companyIdFromRoute(route) {
  if (!route) return null;
  const fromParams = route.params?.id || route.params?.companyId;
  if (fromParams != null && String(fromParams).trim() !== '') {
    const n = parseInt(String(fromParams), 10);
    if (Number.isFinite(n) && n > 0) return n;
  }
  const match = String(route.path || '').match(/\/company\/(\d+)/);
  if (match) {
    const n = parseInt(match[1], 10);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

export function reportLabelForRoute(route) {
  if (!route) return null;
  if (route.meta?.reportLabel) return String(route.meta.reportLabel);
  const name = route.name;
  if (name && REPORT_ACTIVITY_LABELS[name]) return REPORT_ACTIVITY_LABELS[name];
  if (name) {
    return String(name)
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/Report$/, '')
      .trim();
  }
  return null;
}

export function isReportsRoute(route) {
  if (!route) return false;
  if (route.meta?.isReport === true) return true;
  const path = String(route.path || '');
  return /\/reports(\/|$)/i.test(path);
}

/**
 * @param {object} opts
 * @param {number|string} opts.companyId
 * @param {string} opts.reportName
 * @param {'Visited'|'Exported'|'Ran'|'Printed'} [opts.action]
 * @param {string} [opts.reportKey]
 * @param {string} [opts.details]
 * @param {string} [opts.fromDate]
 * @param {string} [opts.toDate]
 * @param {string} [opts.path]
 */
export async function logReportActivity({
  companyId,
  reportName,
  action = 'Visited',
  reportKey = null,
  details = null,
  fromDate = null,
  toDate = null,
  path = null,
} = {}) {
  const cid = parseInt(String(companyId ?? ''), 10);
  const name = String(reportName || '').trim();
  if (!Number.isFinite(cid) || cid <= 0 || !name) return false;
  try {
    await axios.post(`/company/${cid}/activity-logs/report-event`, {
      action,
      reportName: name,
      reportKey: reportKey || name,
      details,
      fromDate,
      toDate,
      path,
    });
    return true;
  } catch (e) {
    logger.warn('logReportActivity failed:', e?.response?.data?.message || e.message);
    return false;
  }
}

/** Fire-and-forget visit log when navigating into a report route. */
export function logReportVisitFromRoute(route) {
  if (!isReportsRoute(route)) return;
  const reportName = reportLabelForRoute(route);
  if (!reportName) return;
  // Skip logging the reports catalog hub if desired — still useful for clarity.
  const companyId = companyIdFromRoute(route);
  if (!companyId) return;
  void logReportActivity({
    companyId,
    reportName,
    action: 'Visited',
    reportKey: route.name || reportName,
    path: route.fullPath || route.path || null,
  });
}

export default {
  REPORT_ACTIVITY_LABELS,
  reportLabelForRoute,
  isReportsRoute,
  logReportActivity,
  logReportVisitFromRoute,
};
